<script lang="ts">
export const elActionButtonSizes = ['base', 'l'] as const;
export type ElActionButtonSizes = (typeof elActionButtonSizes)[number];

export const elActionButtonColors = ['primary', 'secondary'] as const;
export type ElActionButtonColors = (typeof elActionButtonColors)[number];

export const elActionButtonStatus = ['default', 'hover', 'pressed', 'active'] as const;
export type ElActionButtonStatus = (typeof elActionButtonStatus)[number];
</script>

<script setup lang="ts">
import { ElBadge, ElIcon } from '.';
import { ref } from 'vue';

export interface ActionButtonProps {
  id?: string;
  label: string;
  size: ElActionButtonSizes;
  color: ElActionButtonColors;
  status: ElActionButtonStatus;
  leadingIcon?: InstanceType<typeof ElIcon>['$props'];
  trailingIcon?: InstanceType<typeof ElIcon>['$props'] & { name: 'ChevronDownIcon' };
  badge?: Omit<InstanceType<typeof ElBadge>['$props'], 'badge'>;
  onClick?: (() => unknown) | (() => Promise<unknown>);
}

const props = withDefaults(defineProps<ActionButtonProps>(), {
  id: undefined,
  size: 'base',
  color: 'primary',
  status: 'default',
  leadingIcon: undefined,
  trailingIcon: undefined,
  badge: undefined,
  onClick: undefined,
});

const onClick = () => {
  isPressed.value = true;
  $emits('click', props.onClick);
};

const isPressed = ref<boolean>(props.status === 'pressed');
const $emits = defineEmits(['click']);
</script>

<template>
  <button
    class="transition-colors w-fit border flex flex-1 justify-center items-center gap-1 font-semibold leading-6 rounded-lg px-3 hover:bg-neutral-hover hover:text-neutral-darker active:bg-neutral-pressed"
    :class="{
      'text-sm h-8': props.size === 'base',
      'text-base h-10': props.size === 'l',
      'border-transparent text-neutral-darker': props.status === 'default',
      'border-transparent text-neutral-darker bg-neutral-hover': props.status === 'hover',
      'border-transparent bg-neutral-pressed ':
        (isPressed || props.status === 'pressed') && props.status !== 'active' && props.status !== 'default' && props.status !== 'hover',
      'text-primary-active bg-primary-light-active border-primary-active': props.status === 'active' && props.color === 'primary',
      'text-secondary-active bg-secondary-light-active border-secondary-active': props.status === 'active' && props.color === 'secondary',
    }"
    :data-cy="props.label ? props.label.trim().replace(/\s+/g, '') : 'default-action' + '-button'"
    @mouse-down="isPressed = true"
    @click="onClick"
  >
    <ElIcon
      v-if="leadingIcon"
      v-bind="leadingIcon"
      :class="{
        'h-4 w-4': size === 'base',
        'h-5 w-5': size === 'l',
      }"
    />
    <span> {{ props.label }}</span>
    <ElIcon
      v-if="trailingIcon"
      v-bind="trailingIcon"
      :class="{
        'h-4 w-4': size === 'base',
        'h-5 w-5': size === 'l',
      }"
    />
    <div v-if="badge" class="h-4 ml-1 relative flex justify-center items-start">
      <ElBadge v-bind="badge" color="red" />
    </div>
  </button>
</template>
